import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ampli } from '@/ampli';
import { parseAbsoluteToLocal } from '@internationalized/date';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';

import {
  LIMIT_ORDER_EXPIRATIONS,
  LIMIT_ORDER_EXPIRATION_OPTIONS,
  LIMIT_ORDER_EXPIRY_KEY,
  LimitOrderExpiration,
  expirationSelectors,
  useLimitOrderStore,
} from '@/store/limitOrder';

import { buttons } from '@/styles/buttons';
import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';

import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import { InfoCircleSolidSmallIcon } from '@/ui/Icons/InfoCircleSolidSmallIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

const DeferredCustomExpiryModal = dynamic(() =>
  import('../../../Modal/CustomExpiryModal/index').then(
    ({ CustomExpiryModal }) => CustomExpiryModal,
  ),
);

const isValueValidDefaultOption = (value: string | null): value is LimitOrderExpiration =>
  value != null &&
  value !== 'custom' &&
  (LIMIT_ORDER_EXPIRATIONS as readonly string[]).includes(value);

const isValueValidCustomOption = (value: string | null): value is string =>
  value != null && Number.isSafeInteger(Number(value)) && Number(value) > 0;

export const ExpiryDropDownMenu = () => {
  const { base: popoverStyles, item: popoverItemStyles } = popover();
  const [expiryModalOpen, setExpiryModalOpen] = useState(false);
  const { expiration, customExpiration, setExpiration, setCustomExpiration } = useLimitOrderStore(
    expirationSelectors,
    shallow,
  );
  const expirationKeys = Object.keys(LIMIT_ORDER_EXPIRATION_OPTIONS) as LimitOrderExpiration[];

  const handleExpirySelection = (option: LimitOrderExpiration) => {
    if (option === 'custom') {
      setExpiryModalOpen(true);
    } else {
      localStorage.setItem(LIMIT_ORDER_EXPIRY_KEY, option);
      setExpiration(option);
      ampli.limitOrderExpiryUpdated({ limit_order_expiry_type: option });
    }
  };

  // Initialize expiration with value stored on local storage
  useEffect(() => {
    const storedExpiryValue = localStorage.getItem(LIMIT_ORDER_EXPIRY_KEY);

    if (isValueValidDefaultOption(storedExpiryValue)) {
      setExpiration(storedExpiryValue);
    } else if (isValueValidCustomOption(storedExpiryValue)) {
      const localDateNow = parseAbsoluteToLocal(new Date().toISOString());
      const customExpiration = localDateNow.add({ minutes: Number(storedExpiryValue) });
      setCustomExpiration(customExpiration.toDate());
    }
  }, [setExpiration, setCustomExpiration]);

  const dropdownLabel =
    expiration === 'custom'
      ? customExpiration?.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
      : LIMIT_ORDER_EXPIRATION_OPTIONS[expiration];

  return (
    <div>
      <div className="flex flex-col justify-center py-4 px-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-1.5 items-center">
            <p className={fontStyles({ variant: 'LABEL_MD' })}>Expires in</p>
            <MatchaTooltip delayDuration={0}>
              <MatchaTooltipTrigger>
                <span>
                  <InfoCircleSolidSmallIcon className="text-token-gray-700 mt-0.5" />
                </span>
              </MatchaTooltipTrigger>
              <MatchaTooltipContent>
                When a limit order expires, no cancellation fee is charged. In case of manually
                cancelling a limit order, a gas fee is charged at the time of signing the
                transaction.
              </MatchaTooltipContent>
              <MatchaTooltipPopover />
              <MatchaTooltipBottomSheet header="Expires in" />
            </MatchaTooltip>
          </div>
          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger
              className={buttons({
                size: 'small',
                color: 'alphaGray',
                shape: 'rounded',
                class:
                  'justify-between gap-1 m-0 px-2.5 min-w-[143px] rounded-sm before:content-none after:content-none',
              })}
            >
              <span className={fontStyles({ variant: 'LABEL_MD' })}>{dropdownLabel}</span>
              <span className="flex justify-center">
                <ChevronDownIcon />
              </span>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className={popoverStyles({ class: 'mt-0' })} sideOffset={8}>
                {expirationKeys.map((option: LimitOrderExpiration) => {
                  return (
                    <DropdownMenu.Item
                      key={option}
                      className={popoverItemStyles()}
                      onClick={() => handleExpirySelection(option)}
                    >
                      <span className={fontStyles({ variant: 'BODY_SM' })}>
                        {LIMIT_ORDER_EXPIRATION_OPTIONS[option]}
                      </span>
                      {option === expiration && <CheckmarkIcon className="ml-auto" />}
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
      <DeferredCustomExpiryModal
        isOpen={expiryModalOpen}
        onOpenChange={setExpiryModalOpen}
        onClose={() => {
          setExpiryModalOpen(false);
        }}
      />
    </div>
  );
};
